import { useEffect, useRef } from "react";
import { Modal } from "antd";
import {
  buildReloadUrl,
  fetchLatestMainBundlePath,
  getCurrentMainBundlePath,
} from "../utils/appUpdateUtils";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const IDLE_THRESHOLD_MS = 60 * 1000;
const STORAGE_EVENT_KEY = "app:update:latestMainBundle";
const RELOADED_FOR_KEY = "app:update:reloadedFor";

const shouldReloadSilently = (lastInteractionRef) => {
  if (document.hidden) {
    return true;
  }

  const lastInteraction = lastInteractionRef.current || 0;
  return Date.now() - lastInteraction >= IDLE_THRESHOLD_MS;
};

const reloadApp = (latestMainBundlePath) => {
  if (latestMainBundlePath) {
    sessionStorage.setItem(RELOADED_FOR_KEY, latestMainBundlePath);
  }

  window.location.replace(buildReloadUrl());
};

export default function useAppUpdateNotifier() {
  const hasOpenedPromptRef = useRef(false);
  const lastInteractionRef = useRef(Date.now());
  const lastCheckAtRef = useRef(0);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      return undefined;
    }

    const updateLastInteraction = () => {
      lastInteractionRef.current = Date.now();
    };

    const interactionEvents = [
      "click",
      "keydown",
      "mousemove",
      "scroll",
      "touchstart",
    ];
    interactionEvents.forEach((eventName) =>
      window.addEventListener(eventName, updateLastInteraction, {
        passive: true,
      })
    );

    const maybeReload = async ({ force = false } = {}) => {
      const now = Date.now();
      if (!force && now - lastCheckAtRef.current < CHECK_INTERVAL_MS) {
        return;
      }
      lastCheckAtRef.current = now;

      try {
        const currentMainBundlePath = getCurrentMainBundlePath();
        if (!currentMainBundlePath) {
          return;
        }

        const latestMainBundlePath = await fetchLatestMainBundlePath();
        if (!latestMainBundlePath || currentMainBundlePath === latestMainBundlePath) {
          return;
        }

        const alreadyReloadedFor = sessionStorage.getItem(RELOADED_FOR_KEY);
        if (
          shouldReloadSilently(lastInteractionRef) &&
          alreadyReloadedFor !== latestMainBundlePath
        ) {
          reloadApp(latestMainBundlePath);
          return;
        }

        if (!hasOpenedPromptRef.current) {
          hasOpenedPromptRef.current = true;
          localStorage.setItem(
            STORAGE_EVENT_KEY,
            JSON.stringify({
              latestMainBundlePath,
              detectedAt: Date.now(),
            })
          );

          Modal.confirm({
            title: "New version available",
            content:
              "A newer version of this admin panel is available. Refresh now to load the latest updates.",
            okText: "Refresh now",
            cancelText: "Later",
            centered: true,
            onOk: () => reloadApp(latestMainBundlePath),
            onCancel: () => {
              hasOpenedPromptRef.current = false;
            },
          });
        }
      } catch (error) {
        // The checker is best-effort and should never block normal app usage.
      }
    };

    const handleStorageUpdate = (event) => {
      if (event.key !== STORAGE_EVENT_KEY || !event.newValue) {
        return;
      }

      try {
        const parsedValue = JSON.parse(event.newValue);
        const latestMainBundlePath = parsedValue?.latestMainBundlePath;

        if (!latestMainBundlePath) {
          return;
        }

        if (shouldReloadSilently(lastInteractionRef)) {
          reloadApp(latestMainBundlePath);
          return;
        }

        if (!hasOpenedPromptRef.current) {
          hasOpenedPromptRef.current = true;
          Modal.confirm({
            title: "New version available",
            content:
              "A newer version of this admin panel is available. Refresh now to load the latest updates.",
            okText: "Refresh now",
            cancelText: "Later",
            centered: true,
            onOk: () => reloadApp(latestMainBundlePath),
            onCancel: () => {
              hasOpenedPromptRef.current = false;
            },
          });
        }
      } catch (error) {
        // Ignore malformed localStorage payloads from extensions/manual edits.
      }
    };

    const onFocusOrVisible = () => {
      maybeReload();
    };

    const intervalId = window.setInterval(maybeReload, CHECK_INTERVAL_MS);
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);
    window.addEventListener("storage", handleStorageUpdate);

    maybeReload({ force: true });

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
      window.removeEventListener("storage", handleStorageUpdate);
      interactionEvents.forEach((eventName) =>
        window.removeEventListener(eventName, updateLastInteraction)
      );
    };
  }, []);
}
